import React, { useCallback } from "react";
import { Route, Switch, useHistory} from "react-router-dom";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { UPLOAD_SOURCES } from "@cld/upload-widget";
import rootActions, { types as rootAts } from "root/actions";
import TabPanel from "tabs/hocs/TabPanel";
import t from "common/i18n";
import ServiceLoginIntro from "root/components/ServiceLoginIntro";
import WithSidePanel from "root/components/WithSidePanel";
import RouteWithSubRoutes from "root/components/RouteWithSubRoutes";
import FiltersPanel from "../common/components/FiltersPanel";
import actions, { types as ats } from "./actions";
import { useAuth, useUI } from "./selectors";
import Footer from "./components/Footer";
import Header from "./components/Header";
import HomePage from "./components/HomePage";
import { getInternalPath } from "./utils";
import { FILTERS_MAP, FILTERS_SET } from "./data/filtersMap";
import { ROUTES } from "./data/consts";
import SVG_ICONS from "~/assets/icons/icons";

const { UNSPLASH } = UPLOAD_SOURCES;

const {
	[ats.SIGN_IN]: signInAction,
	[ats.SIGN_OUT]: signOutAction,
	[ats.SELECT_ASSETS]: selectAssetsAction,
	[ats.RETRIEVE_ASSETS]: searchAction,
	[ats.CLEAR_SEARCH_PHRASE]: clearSearchPhraseAction,
	[ats.TOGGLE_FILTERS_PANEL]: handleTogglePanelAction,
	[ats.CLEAR_FILTERS]: clearSearchFilters,
	[ats.UPLOAD]: uploadAction,
	[ats.RETRIEVE_ASSET_DETAILS]: retrieveAssetDetailsAction,
} = actions.creators;

const { [rootAts.ADD_UPLOAD_TO_QUEUE]: uploadAssetAction } = rootActions.creators;

const RootBox = styled.div`
	overflow-y: hidden;
	display: block;
	height: 468px;
	@media (max-width: 767px) {
    	height: 500px;
    }
`;
const MainContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
`;

export const Unsplash = ({ routes }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { isSignedIn } = useAuth();
	const { areFiltersSet, isFiltersPanelOpen } = useUI();

	const handleSignIn = useCallback(() => {
		const path = getInternalPath(ROUTES.HOME);
		history.push(path);
		dispatch(signInAction());
	}, [dispatch, history]);

	const handleLogout = useCallback(() => {
		const path = getInternalPath(ROUTES.HOME);
		history.push(path);
		dispatch(signOutAction());
	}, [dispatch, history]);

	const handleSearch = useCallback(
		(params) => {
			const path = getInternalPath(ROUTES.SEARCH);
			if (history.location.pathname !== path) {
				history.push(path);
			}
			dispatch(searchAction(params));
		},
		[history, dispatch]
	);

	const handleClearSearchTerm = useCallback(() => {
		dispatch(clearSearchPhraseAction());
		history.push(getInternalPath(ROUTES.HOME));
	}, [dispatch, history]);

	const handleTogglePanel = useCallback(
		() => dispatch(handleTogglePanelAction()),
		[dispatch]
	);

	const handleClearSearchFilters = useCallback(() => {
		dispatch(clearSearchFilters());
		dispatch(handleSearch({}));
	 },[dispatch, handleSearch]);

	const handleSelectAssets = useCallback((items) => {
		dispatch(selectAssetsAction({ items }));
	}, [dispatch]);

	const handleUploadAction = useCallback(() => {
		dispatch(uploadAction({ uploadAsset: uploadAssetAction }));
	}, [dispatch]);

	const handleRetrieveAsset = useCallback((id) => {
		dispatch(retrieveAssetDetailsAction(id));
	}, [dispatch]);

	const sidePanel = useCallback(() => (
			<FiltersPanel
				source={UNSPLASH}
				handleSearch={handleSearch}
				clearSearchFilters={handleClearSearchFilters}
				areFiltersSet={areFiltersSet}
				isFiltersPanelOpen={isFiltersPanelOpen}
				filtersMap={FILTERS_MAP}
				filterSet={FILTERS_SET}
			/>
		),
		[handleSearch, handleClearSearchFilters, areFiltersSet, isFiltersPanelOpen]
	);

	return (
		<RootBox>
			{!isSignedIn && (
				<MainContainer>
					<ServiceLoginIntro
						key="unsplash_login_intro"
						title={t("unsplash.no_auth_title")}
						actionTitle={t("unsplash.no_auth_action")}
						statement={t("unsplash.no_auth_statement")}
						dataTest="connect-to-unsplash"
						btnIcon={SVG_ICONS.UNSPLASH}
						actionHandler={handleSignIn}
					/>
				</MainContainer>
			)}
			{isSignedIn && (
				<MainContainer>
					<WithSidePanel sidePanel={sidePanel} openSidePanel={isFiltersPanelOpen}>
						<div>
							<Header
								handleLogout={handleLogout}
								handleSearch={handleSearch}
								handleClearSearchTerm={handleClearSearchTerm}
								handleTogglePanel={handleTogglePanel}
								isFiltersPanelOpen={isFiltersPanelOpen}
								areFiltersSet={areFiltersSet}
							/>
							<Switch>
								<Route
									exact
									path={getInternalPath(ROUTES.HOME)}
									render={() => <HomePage handleSearch={handleSearch} onSelect={handleSelectAssets} />}
								/>
								<SubRoutes
									routes={routes}
									handleSearch={handleSearch}
									onSelect={handleSelectAssets}
									retrieveAsset={handleRetrieveAsset}
								/>
							</Switch>
						</div>
					</WithSidePanel>
				</MainContainer>
			)}
			<Footer togglePanel={handleTogglePanel} uploadAsset={handleUploadAction} />
		</RootBox>
	);
};

export default TabPanel({
	id: UNSPLASH,
})(Unsplash);

export const SubRoutes = ({ routes, handleSearch, onSelect, retrieveAsset, isMultiple = false }) => {
	return routes.map((r: {| path: string |}): Object => (
		<RouteWithSubRoutes
			key={`st_route_${r.path}`}
			{...r}
			handleSearch={handleSearch}
			onSelect={onSelect}
			retrieveAsset={retrieveAsset}
			isMultiple={isMultiple}
		/>
	));
};
